import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import { luxLoyaltyRegions, updateLuxLoyaltyAccountRegion } from '~/services/LuxLoyalty';

import RegionSwitchConfirmDialog from './RegionSwitchConfirmDialog';

interface Props {
  /** The Lux Loyalty account information */
  account: App.LuxLoyaltyAccount;
  /** Callback function triggered when the region is successfully updated */
  onRegionUpdated?: (newRegion: string) => void;
}

type ValidRegionCode = keyof typeof luxLoyaltyRegions;

// Type guard to check if a string is a valid region code
function isValidRegionCode(region: string): region is ValidRegionCode {
  return region in luxLoyaltyRegions;
}

const formattedRegions = Object.entries(luxLoyaltyRegions).map(([regionCode, regionLabel]) => ({
  value: regionCode,
  label: regionLabel,
}));

/**
 * Component to switch the region of a Lux Loyalty account
 * Only visible to admin and ICS team members
 * @param account - The Lux Loyalty account to update
 * @param onRegionUpdated - Optional callback when region is updated
 */
export default function RegionSwitcher({ account, onRegionUpdated }: Props) {
  const [selectedRegion, setSelectedRegion] = useState<ValidRegionCode>(
    isValidRegionCode(account.region) ? account.region : 'AU',
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  /**
   * TanStack Query mutation for updating the region
   * Handles loading, success, and error states automatically
   */
  const updateRegionMutation = useMutation({
    mutationFn: async (newRegion: ValidRegionCode) => {
      return await updateLuxLoyaltyAccountRegion(account.customerId, newRegion);
    },
    // Close the dialog either way so the success/error Alert below the switcher is visible
    onSettled: () => {
      setIsConfirmOpen(false);
    },
    onSuccess: (_, newRegion) => {
      // Trigger callback if provided
      onRegionUpdated?.(newRegion);
    },
  });

  /**
   * Opens the confirmation dialog for the selected region.
   * Validates the selection first so the prompt is never shown for a no-op switch.
   */
  const handleSwitchRegion = () => {
    // Don't proceed if no region selected or it's the same as current
    if (!selectedRegion || selectedRegion === account.region) {
      return;
    }

    setIsConfirmOpen(true);
  };

  /** Performs the switch once the agent has acknowledged the consequences */
  const handleConfirmSwitch = () => {
    updateRegionMutation.mutate(selectedRegion);
  };

  // Fall back to the raw code when the account holds a region we don't have a label for
  const currentRegionLabel = isValidRegionCode(account.region) ? luxLoyaltyRegions[account.region] : account.region;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Switch region to
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="region-select-label">Region</InputLabel>
          <Select
            labelId="region-select-label"
            value={selectedRegion}
            label="Region"
            onChange={(e) => {
              const value = e.target.value;
              if (isValidRegionCode(value)) {
                setSelectedRegion(value);
              }
            }}
            disabled={updateRegionMutation.isPending}
          >
            {formattedRegions.map((region) => (
              <MenuItem key={region.value} value={region.value}>
                {region.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleSwitchRegion}
          disabled={updateRegionMutation.isPending || !selectedRegion || selectedRegion === account.region}
        >
          {updateRegionMutation.isPending ? 'Switching...' : 'Switch'}
        </Button>
      </Stack>
      {updateRegionMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {updateRegionMutation.error.message}
        </Alert>
      )}
      {updateRegionMutation.isSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Region updated successfully to {formattedRegions.find((r) => r.value === selectedRegion)?.label}
        </Alert>
      )}
      <RegionSwitchConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmSwitch}
        currentRegionLabel={currentRegionLabel}
        newRegionLabel={luxLoyaltyRegions[selectedRegion]}
        isSubmitting={updateRegionMutation.isPending}
      />
    </Box>
  );
}
